// ABOUTME: View component for API connections in the relation environment.
// ABOUTME: Displays a data grid of webshopcredentials with webshop_type "api".
import { Box, useTheme } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { tokens } from "../../../../../theme";
import Text from "../../../../../components/theme/text/Text";
import ShLabel from "../../../../../components/theme/label/ShLabel";
import ShLabelSkeleton from "../../../../../components/theme/skeleton/ShLabelSkeleton";
import AddIcon from '@mui/icons-material/Add';
import IntegrationInstructionsOutlinedIcon from '@mui/icons-material/IntegrationInstructionsOutlined';
import { useTranslation } from "react-i18next";
import MAddApiConnection from "../../../../../components/global/Modals/MAddApiConnection";
import ShCopyButton from "../../../../../components/theme/ShCopy";
import { RelationWebshopIntegrationCard } from "../../../../../components/global/cards/other/WebshopIntegrationCard";
import ButtonGroup from "../../../../../components/theme/buttons/ButtonGroup";
import Shbutton from "../../../../../components/theme/buttons/Shbutton";
import ShDataGrid2 from "../../../../../components/theme/datagrid2/ShDataGrid2";
import SidepanelLoader from "../../../../../components/theme/sidepanel2/SidepanelLoader";
import ShTextSkeleton from "../../../../../components/theme/skeleton/ShTextSkeleton";
import { useGetWebshopCredentialsQuery } from "../../../../../newapi/webshop/webshopSlice";
import { selectUserPermission } from "../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from "../../../../../utils/permissions";
import { useSelector } from "react-redux";
import ShDataGrid3 from "../../../../../components/theme/datagrid3/ShDataGrid3";

const SpApiConnectionDetails = SidepanelLoader(() => import("../../../../../components/global/Sidepanels/SpApiConnectionDetails/SpApiConnectionDetails"));

const GridActions = (props) => {
    const theme = useTheme();
    const { t } = useTranslation();
    return (
        <ButtonGroup version={2}>
            {props.hasCreatePermission && (
                <Shbutton
                    onClick={() => props.handleOpen(true)}
                    className={"group-btn"}
                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                    variant="contained"
                    endIcon={<AddIcon/>}>{t("new_api_connection")}</Shbutton>
            )}
        </ButtonGroup>
    );
}

let gridOptions = {
    export: true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
};

const All = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [data, setData] = useState([]);
    const [isExtended, setIsExtended] = useState(false);
    const [config, setConfig] = useState({"webshop_type": "api"});
    const [spLoading, setSpLoading] = useState();

    const hasCreatePermission = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.RP_WEBSHOPCREDENTIALS_MANAGE])
    );

    const [addModalOpen, setAddModalOpen] = useState(false);
    const addModal = {
        open: addModalOpen,
        setOpen: setAddModalOpen
    }

    const handleClose = () => {
        setAddModalOpen(false);
    };

    const [apiConnectionId, setApiConnectionId] = useState();
    const apiConnectionIdRef = useRef(apiConnectionId);

    const getApiConnectionDetails = (params) => {
        if (!params) return;

        const newId = parseInt(params.row.id);
        if (apiConnectionIdRef.current !== newId) {
            if (!spLoading) {
                setSpLoading(true);
            }
            setApiConnectionId(newId);
            apiConnectionIdRef.current = newId;
        }
        setIsExtended(true);
    };

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions: gridOptions,
            mobileActions: <GridActions hasCreatePermission={hasCreatePermission} handleOpen={() => setAddModalOpen(true)}></GridActions>
        });

        return () => {}
    }, []);

    const columns = [
        {
            field: "description", headerName: t("name"), flex: 2, cellClassName: "webshop", renderCell: (cellValues) => {
                if (cellValues.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return (
                    <>
                        <Box sx={{ paddingRight: '10px', display: "flex", alignItems: "center", color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400] }}>
                            <IntegrationInstructionsOutlinedIcon fontSize="small"/>
                        </Box>
                        <Text semibold variant={theme.palette.mode === "dark" ? "blue" : "primary"}>{cellValues.value}</Text>
                    </>
                )
            }
        },
        {
            field: "token", headerName: t("token"), flex: 2, cellClassName: "token", renderCell: (cellValues) => {
                if (cellValues.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return (
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        overflow: "hidden",
                        "&:hover .copyel": {
                            opacity: 1,
                        }
                    }}>
                        <Text noBreak>{cellValues.value}</Text>
                        <ShCopyButton
                            styles={{button: {
                                padding: "2px",
                                background: "transparent",
                                color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                                "&:hover": {
                                    background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[200],
                                }
                            }}}
                            sx={{ opacity: 0, transition: "opacity 200ms", flexShrink: 0 }}
                            className={"copyel"}
                            closeAfter={1500}
                            value={cellValues.value}
                        />
                    </Box>
                )
            }
        },
        {
            field: "phoneView",
            headerName: "PhoneView",
            cellClassName: "phone-column--cell",
            renderCell: (params) => {
                return (
                    <RelationWebshopIntegrationCard onClick={() => getApiConnectionDetails(params)} skeleton={params.row.skeleton ?? false} {...params.row}/>
                )
            }
        },
        {
            field: "state",
            headerName: t("status"),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShLabelSkeleton/>
                }

                const labelColor = params.row.state === "active" ? "green" : theme.palette.mode === "dark" ? "greyLight" : "grey";

                return (
                    <ShLabel size={32} palette="normal" variant={labelColor}>
                        {t(params.row.state)}
                    </ShLabel>
                );
            }
        },
    ];

    return (
        <Box
            sx={{
                height: 1,
                "@media screen and (max-width: 47.9375em)": {
                    paddingTop: 0,
                    paddingX: 0,
                }
            }}
        >
            <ShDataGrid3 
                gridKey="relations.integrations.apiconnections"
                disableViewSaving
                key={theme.palette.mode}
                content={useGetWebshopCredentialsQuery}
                config={config}
                gridActions={<GridActions hasCreatePermission={hasCreatePermission} handleOpen={() => setAddModalOpen(true)}></GridActions>}
                title={t("api_connections")}
                gridOptions={gridOptions}
                sortModel={{field: columns[0].field, type: "ASC"}}
                columns={columns}
                rows={data}
                onRowClick={getApiConnectionDetails}>
            </ShDataGrid3>

            <MAddApiConnection onOpen={() => addModal.setOpen(true)} onClose={() => addModal.setOpen(false)} open={addModal.open} handleClose={handleClose}/>

            {spLoading &&
                <SpApiConnectionDetails
                    isExtended={isExtended}
                    setIsExtended={setIsExtended}
                    apiConnectionId={apiConnectionId}
                />
            }
        </Box>
    )
};

export default All;
